import { useEffect } from 'react';
import { Stack, useRouter, useSegments } from 'expo-router';
import { useAuth } from '../hooks/useAuth';
import { ActivityIndicator, View } from 'react-native';
import { CrossPlatformAlertProvider } from '../components/CrossPlatformAlert';
import { LanguageProvider } from '../contexts/LanguageContext';
import * as Sentry from '@sentry/react-native';
import { useAssets } from 'expo-asset';
import WebUpdateBanner from '../components/WebUpdateBanner';

Sentry.init({
  dsn: process.env.EXPO_PUBLIC_SENTRY_DSN,
  enabled: !!process.env.EXPO_PUBLIC_SENTRY_DSN,
  integrations: [
    Sentry.reactNativeTracingIntegration(),
    Sentry.browserReplayIntegration(),
    Sentry.feedbackIntegration({
      colorScheme: "system",
    }),
  ],
  enableLogs: true,
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

function RootLayout() {
  const [, assetError] = useAssets([
    require('../assets/images/icon.png'),
    require('../assets/images/favicon.png'),
    require('../assets/images/adaptive-icon.png'),
    require('../assets/images/splash-icon.png'),
    require('../assets/images/redmed-logo.png'),
  ]);

  if (__DEV__ && assetError) {
    console.warn('[Assets] Failed to load assets:', assetError);
  }

  const { firebaseUser, appUser, loading } = useAuth();
  const router = useRouter();
  const segments = useSegments();

  useEffect(() => {
    if (loading) return;
    const inAuthGroup = segments[0] === '(auth)';
    if (!firebaseUser && !inAuthGroup) {
      router.replace('/(auth)/login');
    } else if (firebaseUser && inAuthGroup) {
      router.replace('/(app)/home');
    }
  }, [firebaseUser, loading, segments, router]);

  useEffect(() => {
    if (loading) return;
    if (appUser) {
      Sentry.setUser({
        id: appUser.uid,
        email: appUser.email,
        role: appUser.role,
      });
      if (__DEV__) console.log('[Sentry] User context set for authenticated user:', appUser.uid);
    } else {
      Sentry.setUser({
        id: 'guest',
      });
      if (__DEV__) console.log('[Sentry] User context set to guest');
    }
  }, [appUser, loading]);

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  return (
    <CrossPlatformAlertProvider>
      <LanguageProvider>
        <View style={{ flex: 1 }}>
          <WebUpdateBanner />
          <Stack screenOptions={{ headerShown: false }} />
        </View>
      </LanguageProvider>
    </CrossPlatformAlertProvider>
  );
}

export default Sentry.wrap(RootLayout);
